import { data } from "./data";
import { applyExperienceOverride, applyOverride, isPublished } from "./overlay";
import type {
  Experience,
  IndustryTag,
  LensKey,
  Project,
  QualificationTag,
  RoleTag,
  SkillTag,
  Tag,
  TechnologyTag,
} from "../types";

// Draft project narratives are honest placeholders pending Srinath's editorial pass (CLAUDE.md), so
// they are hidden from every PUBLIC listing surface (lens grids, graph, related, counts) until their
// status flips to published. Direct /project/:id URLs still resolve via getProject() so a draft stays
// previewable by link. Set VITE_SHOW_DRAFTS=true to surface drafts everywhere (e.g. local editing).
const SHOW_DRAFTS = import.meta.env.VITE_SHOW_DRAFTS === "true";
// Effective publication: a live overlay stage (admin edit) wins over the baked YAML status.
const isPublic = (p: Project): boolean => SHOW_DRAFTS || isPublished(p.id, p.status);
const publishedProjects = (): Project[] => data.projects.filter(isPublic).map(applyOverride);

export function getProfile() {
  return data.profile;
}

export function getExperiences(): Experience[] {
  return data.experiences.map(applyExperienceOverride);
}

export function getExperience(id: string): Experience | undefined {
  const e = data.experiences.find((q) => q.id === id);
  return e ? applyExperienceOverride(e) : undefined;
}

export function getProjects(): Project[] {
  return publishedProjects();
}

export function getProject(id: string): Project | undefined {
  const p = data.projects.find((q) => q.id === id);
  return p ? applyOverride(p) : undefined;
}

export function getProjectsForExperience(experienceId: string): Project[] {
  return publishedProjects().filter((p) => p.experienceId === experienceId);
}

export function getEducation() {
  return data.education;
}

export function getTags(lens: LensKey): Tag[] {
  switch (lens) {
    case "industry":
      return data.industries;
    case "technology":
      return data.technologies;
    case "skill":
      return data.skills;
    case "role":
      return data.roles;
    case "timeline":
      return timelineBuckets();
  }
}

export function getIndustry(id: string): IndustryTag | undefined {
  return data.industries.find((t) => t.id === id);
}

export function getTechnology(id: string): TechnologyTag | undefined {
  return data.technologies.find((t) => t.id === id);
}

export function getSkill(id: string): SkillTag | undefined {
  return data.skills.find((t) => t.id === id);
}

export function getRole(id: string): RoleTag | undefined {
  return data.roles.find((t) => t.id === id);
}

export function getQualifications(): QualificationTag[] {
  return data.qualifications;
}

export function tagLabel(lens: LensKey, id: string): string {
  const tag = getTags(lens).find((t) => t.id === id);
  return tag?.label ?? id;
}

export function experiencesByTag(lens: LensKey, tagId: string): Experience[] {
  return data.experiences.filter((e) => experienceHasTag(e, lens, tagId));
}

export function projectsByTag(lens: LensKey, tagId: string): Project[] {
  return publishedProjects().filter((p) => projectHasTag(p, lens, tagId));
}

export function experienceHasTag(e: Experience, lens: LensKey, tagId: string): boolean {
  if (lens === "industry") return e.industries.includes(tagId);
  if (lens === "technology") return e.technologies.includes(tagId);
  if (lens === "skill") return e.skills.includes(tagId);
  if (lens === "role") return (e.roleIds ?? []).includes(tagId);
  if (lens === "timeline") return timelineBucketFor(e.start, e.end) === tagId;
  return false;
}

export function projectHasTag(p: Project, lens: LensKey, tagId: string): boolean {
  if (lens === "industry") return p.industries.includes(tagId);
  if (lens === "technology") return p.technologies.includes(tagId);
  if (lens === "skill") return p.skills.includes(tagId);
  if (lens === "role") {
    const exp = getExperience(p.experienceId);
    return (exp?.roleIds ?? []).includes(tagId);
  }
  if (lens === "timeline") {
    const exp = getExperience(p.experienceId);
    if (!exp) return false;
    return timelineBucketFor(exp.start, exp.end) === tagId;
  }
  return false;
}

const TIMELINE_BUCKETS: { id: string; label: string; from: number; to: number }[] = [
  // Reverse chronological — most recent first.
  { id: "2020-present", label: "2020–present · Deloitte CCaaS", from: 2020, to: 2100 },
  { id: "2015-2020", label: "2015–2020 · MetLife + SJ Enterprise + Cisco", from: 2015, to: 2020 },
  { id: "2010-2015", label: "2010–2015 · BofA Merrill + BCBS NC", from: 2010, to: 2015 },
  { id: "2005-2010", label: "2005–2010 · IBM Global Services + Verizon", from: 2005, to: 2010 },
  { id: "2000-2005", label: "2000–2005 · IBM Software Group", from: 2000, to: 2005 },
  { id: "1996-2000", label: "1996–2000 · Foundations", from: 1996, to: 2000 },
];

export function timelineBuckets(): Tag[] {
  return TIMELINE_BUCKETS.map((b) => ({ id: b.id, label: b.label }));
}

function yearOf(d: string): number {
  if (!d || d === "present") return new Date().getFullYear();
  return parseInt(d.slice(0, 4), 10);
}

export function timelineBucketFor(start: string, end: string): string {
  const sy = yearOf(start);
  const ey = yearOf(end);
  // Bucket by the experience's center-of-mass year.
  const center = Math.floor((sy + ey) / 2);
  const match = TIMELINE_BUCKETS.find((b) => center >= b.from && center < b.to);
  return match?.id ?? TIMELINE_BUCKETS[TIMELINE_BUCKETS.length - 1].id;
}

export function relatedProjects(p: Project, limit = 4): Project[] {
  const score = (q: Project) => {
    if (q.id === p.id) return -1;
    let s = 0;
    s += overlap(p.industries, q.industries) * 3;
    s += overlap(p.technologies, q.technologies) * 2;
    s += overlap(p.skills, q.skills) * 1;
    return s;
  };
  return publishedProjects()
    .map((q) => ({ q, s: score(q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.q);
}

function overlap<T>(a: T[], b: T[]): number {
  const set = new Set(a);
  return b.filter((x) => set.has(x)).length;
}

export type GraphNode = {
  id: string;
  label: string;
  type: "experience" | "project" | "industry" | "technology" | "skill";
};
export type GraphEdge = { source: string; target: string };
export type GraphData = { nodes: GraphNode[]; edges: GraphEdge[] };

export function graphData(): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const pub = publishedProjects();
  const pubIds = new Set(pub.map((p) => p.id));
  const push = (n: GraphNode) => nodes.push(n);
  data.experiences.forEach((e) =>
    push({ id: `exp:${e.id}`, label: e.company, type: "experience" }),
  );
  pub.forEach((p) =>
    push({ id: `proj:${p.id}`, label: p.title, type: "project" }),
  );
  data.industries.forEach((t) =>
    push({ id: `ind:${t.id}`, label: t.label, type: "industry" }),
  );
  data.technologies.forEach((t) =>
    push({ id: `tech:${t.id}`, label: t.label, type: "technology" }),
  );
  data.skills.forEach((t) =>
    push({ id: `skill:${t.id}`, label: t.label, type: "skill" }),
  );
  data.experiences.forEach((e) => {
    e.industries.forEach((t) => edges.push({ source: `exp:${e.id}`, target: `ind:${t}` }));
    e.technologies.forEach((t) => edges.push({ source: `exp:${e.id}`, target: `tech:${t}` }));
    e.skills.forEach((t) => edges.push({ source: `exp:${e.id}`, target: `skill:${t}` }));
    (e.projects ?? []).filter((pid) => pubIds.has(pid)).forEach((pid) =>
      edges.push({ source: `exp:${e.id}`, target: `proj:${pid}` }),
    );
  });
  pub.forEach((p) => {
    p.industries.forEach((t) => edges.push({ source: `proj:${p.id}`, target: `ind:${t}` }));
    p.technologies.forEach((t) =>
      edges.push({ source: `proj:${p.id}`, target: `tech:${t}` }),
    );
    p.skills.forEach((t) => edges.push({ source: `proj:${p.id}`, target: `skill:${t}` }));
  });
  return { nodes, edges };
}

export function expsByRecency(): Experience[] {
  return data.experiences.map(applyExperienceOverride).sort((a, b) => {
    const ay = a.end === "present" ? 9999 : yearOf(a.end);
    const by = b.end === "present" ? 9999 : yearOf(b.end);
    return by - ay;
  });
}

// ─── Detail-page navigation (Previous / Next + editor Save & Next) ──────────
// Experiences are ordered chronologically (oldest -> newest), so "Previous" is the older one and
// "Next" is the newer one; the oldest hides Previous and the newest hides Next. Engagements walk the
// curated order within their parent experience.

function expsChronological(): Experience[] {
  return expsByRecency().slice().reverse();
}

/** Older (prev) and newer (next) experience for the detail pager. No wrap. */
export function adjacentExperience(id: string): { prev?: Experience; next?: Experience } {
  const list = expsChronological();
  const i = list.findIndex((e) => e.id === id);
  if (i < 0) return {};
  return { prev: list[i - 1], next: list[i + 1] };
}

/** Next (newer) experience, wrapping newest -> oldest. For the editor's Save & Next. */
export function nextExperienceWrap(id: string): Experience | undefined {
  const list = expsChronological();
  const i = list.findIndex((e) => e.id === id);
  if (i < 0 || list.length === 0) return undefined;
  return list[(i + 1) % list.length];
}

/** Prev / next engagement within the same experience (respects publication for the viewer). No wrap. */
export function adjacentEngagement(projectId: string): { prev?: Project; next?: Project } {
  const p = getProject(projectId);
  if (!p) return {};
  const sibs = getProjectsForExperience(p.experienceId);
  const i = sibs.findIndex((x) => x.id === projectId);
  if (i < 0) return {};
  return { prev: sibs[i - 1], next: sibs[i + 1] };
}

/** Next engagement within the same experience, wrapping. Admin editor: walks every engagement. */
export function nextEngagementWrap(projectId: string): Project | undefined {
  const p = data.projects.find((x) => x.id === projectId);
  if (!p) return undefined;
  const sibs = data.projects.filter((x) => x.experienceId === p.experienceId).map(applyOverride);
  const i = sibs.findIndex((x) => x.id === projectId);
  if (i < 0 || sibs.length === 0) return undefined;
  return sibs[(i + 1) % sibs.length];
}

export function computeYears(): number {
  if (!data.experiences.length) return 0;
  const minStart = Math.min(...data.experiences.map((e) => yearOf(e.start)));
  return new Date().getFullYear() - minStart;
}

export interface IndustryCount {
  id: string;
  label: string;
  count: number;
  share: number;
}

export function industryCounts(): IndustryCount[] {
  const counts = new Map<string, number>();
  for (const e of data.experiences) {
    for (const id of e.industries) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  for (const p of publishedProjects()) {
    for (const id of p.industries) counts.set(id, (counts.get(id) ?? 0) + 0.5);
  }
  const max = Math.max(1, ...Array.from(counts.values()));
  return Array.from(counts.entries())
    .map(([id, count]) => {
      const tag = data.industries.find((t) => t.id === id);
      return { id, label: tag?.label ?? id, count: Math.round(count), share: count / max };
    })
    .sort((a, b) => b.count - a.count);
}

export interface TechUsage {
  id: string;
  label: string;
  category: string;
  weight: number;
  last: number;
}

export function technologyUsage(): TechUsage[] {
  const now = new Date().getFullYear();
  return data.technologies.map((t) => {
    let last = 0;
    let weight = 0;
    for (const e of data.experiences) {
      if (!e.technologies.includes(t.id)) continue;
      const ey = e.end === "present" ? now : yearOf(e.end);
      last = Math.max(last, ey);
      weight += 1 + Math.max(0, ey - yearOf(e.start));
    }
    for (const p of publishedProjects()) {
      if (!p.technologies.includes(t.id)) continue;
      const e = getExperience(p.experienceId);
      if (e) {
        const ey = e.end === "present" ? now : yearOf(e.end);
        last = Math.max(last, ey);
      }
      weight += 0.5;
    }
    return { id: t.id, label: t.label, category: t.category ?? "", weight, last };
  }).filter((t) => t.weight > 0);
}

export function yearRange(start: string, end: string): string {
  const sy = start.slice(0, 4);
  const ey = end === "present" ? "Present" : end.slice(0, 4);
  return `${sy}–${ey}`;
}

export function formatDateRange(start: string, end: string): string {
  const fmt = (d: string) => {
    if (d === "present") return "Present";
    const [y, m] = d.split("-");
    if (!m) return y;
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleString("en-US", { month: "short", year: "numeric" });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
